import { ShopItemTile } from "./ShopItemTile";
import type { ShopCatalogEntry, ShopItemFilter, ShopItemGroup } from "./types";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@ui/button";

import { cn } from "@/lib/utils";
import type { StafferPetRarity } from "@/types";

// Shop first so new stuff is the first thing people see. Exclusive and retired
// items stay hidden: they're mostly one-off customs for a single person.
const groups: {
  id: string;
  label: string;
  filters: ShopItemFilter[];
  // Worn items fold into Owned; the tile still says "Wearing".
  itemGroups: ShopItemGroup[];
}[] = [
  {
    id: "FOR_SALE",
    label: "For sale",
    filters: ["ALL", "SHOP"],
    itemGroups: ["FOR_SALE"],
  },
  {
    id: "OWNED",
    label: "Owned",
    filters: ["ALL", "OWNED"],
    itemGroups: ["WEARING", "OWNED"],
  },
];
const rarityRanks: Record<StafferPetRarity, number> = {
  COMMON: 0,
  UNCOMMON: 1,
  RARE: 2,
  EPIC: 3,
  LEGENDARY: 4,
};

// Exclusives lead, then rarest first; ties keep catalog order (sort is stable).
const sortRarestFirst = (entries: ShopCatalogEntry[]): ShopCatalogEntry[] =>
  [...entries].sort((a, b) => {
    const exclusiveDelta = Number(isExclusive(b)) - Number(isExclusive(a));
    if (exclusiveDelta !== 0) return exclusiveDelta;
    return rarityRanks[b.rarity] - rarityRanks[a.rarity];
  });

const isExclusive = (entry: ShopCatalogEntry): boolean =>
  entry.ownedLabel === "Exclusive" || entry.ownedLabel === "Retired";

const filters: { id: ShopItemFilter; label: string }[] = [
  { id: "ALL", label: "All" },
  { id: "OWNED", label: "Owned" },
  { id: "SHOP", label: "Shop" },
];

export const ShopCatalogGrid = ({
  coinBalance,
  description,
  entries,
  filter,
  title,
  onFilterChange,
  onSelect,
}: {
  coinBalance: number;
  description: string;
  entries: ShopCatalogEntry[];
  filter: ShopItemFilter;
  title: string;
  onFilterChange: (filter: ShopItemFilter) => void;
  onSelect: (id: string) => void;
}) => {
  /* Helpers */
  const visibleGroups = groups
    .filter((group) => group.filters.includes(filter))
    .map((group) => ({
      ...group,
      entries: sortRarestFirst(
        entries.filter((entry) => group.itemGroups.includes(entry.group)),
      ),
    }))
    .filter((group) => group.entries.length > 0);

  /* Render */
  const render = () => {
    return (
      <Column className="min-h-0 min-w-0 flex-1 gap-3">
        <Row className="flex-wrap items-end justify-between gap-2 border-b-4 border-slate-900 pb-3">
          <Column className="gap-1">
            <Text className="font-mono text-sm font-black uppercase tracking-[0.14em] text-slate-900">
              {title}
            </Text>
            <Text className="font-mono text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">
              {description}
            </Text>
          </Column>
          <Row className="gap-1">
            {filters.map((option) => (
              <Button
                key={option.id}
                type="button"
                variant="outlineDark"
                onClick={() => {
                  onFilterChange(option.id);
                }}
                className={cn(
                  "h-7 rounded-none border-2 border-slate-900 px-2 font-mono text-[9px] font-black uppercase tracking-[0.1em]",
                  filter === option.id
                    ? "bg-slate-900 text-orange-50 hover:bg-slate-800 hover:text-orange-50"
                    : "bg-white text-slate-900 hover:bg-orange-50",
                )}
              >
                {option.label}
              </Button>
            ))}
          </Row>
        </Row>
        <Column className="min-h-0 gap-4 overflow-y-auto pb-2 pl-0.5 pr-2 pt-0.5">
          {visibleGroups.map((group) => (
            <Column key={group.id} className="gap-2">
              <Row className="items-center gap-2">
                <Text className="shrink-0 font-mono text-[10px] font-black uppercase tracking-[0.16em] text-slate-900">
                  {group.label} · {group.entries.length.toString()}
                </Text>
                <Column className="h-0 flex-1 border-t-2 border-dotted border-slate-300" />
              </Row>
              <div className="grid grid-cols-[repeat(auto-fill,minmax(104px,1fr))] gap-3">
                {group.entries.map((entry) => (
                  <ShopItemTile
                    key={entry.id}
                    coinBalance={coinBalance}
                    entry={entry}
                    onSelect={onSelect}
                  />
                ))}
              </div>
            </Column>
          ))}
        </Column>
      </Column>
    );
  };

  return render();
};
